import { DP_PROVIDERS } from "./desired-state.js";
import { LOWERCASED_UUID_PATTERN } from "./deployment-config.js";
import { blobBindingsFileSchema, blobBucketPhysicalSchema } from "./blob-bindings.js";
import { BLOB_BUCKETS_PER_APP_MAX, BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME, BLOB_BUCKET_DURABILITIES, BLOB_BUCKET_GENERATION_MAX, PHYSICAL_BUCKET_NAME_PATTERN, blobBucketNameSchema, dataLocationSchema, physicalBucketName } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/relocation.ts
/**
* The relocation contract of the blob buckets plan (2026-10-13-blob-buckets, stage 4a, R-079).
*
* A relocation plan moves every app bucket of one (data plane, org, app) to a new location. Three
* parties read this module:
*
* - provisioning-svc (WS-19) owns the plan engine. It serves the platform-admin routes under
*   {@link RELOCATIONS_ROUTE} with the bodies {@link RelocationCreateBodySchema},
*   {@link RelocationApproveBodySchema} and {@link RelocationAbortBodySchema}, and it answers the
*   plan view {@link RelocationPlanViewSchema}. It keeps the plan in `provisioning.relocations` and
*   `provisioning.relocation_steps` (R-079 item 2, I-1).
* - release-engine (WS-48) is the one writer of `release.blob_bucket_bindings`. It serves the six
*   internal routes under {@link BLOB_RELOCATION_ROUTE} behind `ProvisioningServiceTokenGuard`. Each
*   route answers {@link BlobRelocationAnswerSchema} or one coded refusal of
*   {@link BLOB_RELOCATION_ERRORS}.
* - provisioning-svc calls those routes through the port {@link ReleaseEngineRelocationClient}.
*   Its tests use {@link FakeReleaseEngineRelocationClient}, the complete in-memory twin of the
*   six routes (defaults row F9).
*
* R-058 and R-079 item 3: a plan names app bucket rows only. Every body of this module carries a
* required `appSlug` in the app slug grammar, and no member can hold `null` or a platform kind. So
* no route of this contract can name a `release.platform_buckets` row, and an org `audit` bucket
* always keeps generation 1.
*
* Every org id here is the CP org id (`platform.organizations.id`), the key of
* `release.blob_bucket_bindings.org_id` since R-060.
*
* The legacy import (stage 6, R-100) is a second mover kind, `legacy_import`. It copies the objects
* under named prefixes of the shared `app-fixtures` bucket of a one-org GCP data plane into the
* durable org buckets of one app, and then switches the app off the shared bucket:
*
* - provisioning-svc (WS-25b) takes {@link LegacyImportCreateBodySchema} on the create route, picks
*   the one active org of the data plane, and runs the eight {@link RELOCATION_STEPS} with
*   {@link LEGACY_IMPORT_SKIPPED_STEPS} marked `skipped`.
* - release-engine (WS-25a) serves the internal routes under {@link BLOB_LEGACY_IMPORT_ROUTE}. Each
*   answers {@link BlobLegacyImportAnswerSchema}: the shared source bucket, the hold flag of the app,
*   the durable target buckets and the declared durable names with no bucket yet (R-111). The hold
*   keeps the app on its legacy store until the switch (R-100 item 4).
* - The port {@link ReleaseEngineLegacyImportClient} carries the five import methods beside the six
*   methods of {@link ReleaseEngineRelocationClient} (R-107 item 6), and
*   {@link FakeReleaseEngineRelocationClient} carries both kinds.
*
* Strict at every depth. No `.transform()`, so `createOpenApiDocument()` can represent each schema.
* The schemas and the path builder are pure. The Fake keeps its state in memory and makes no call
* to a network, a file system or a clock.
*/
/**
* The kinds of mover a plan may name (`overview.md` section 2.6):
*
* - `blob`: the GCS mover of provisioning-svc moves every app bucket of one (data plane, org, app)
*   to a new location (R-079 I-2).
* - `legacy_import`: the same mover copies the objects under named prefixes of the shared
*   `app-fixtures` bucket into the durable org buckets of one app, then switches the app off the
*   shared bucket (R-100). Its create body is {@link LegacyImportCreateBodySchema}.
*
* The plan format carries the kind so later movers (Postgres, Redis, NATS) can attach.
*/
const RELOCATION_MOVER_KINDS = ["blob", "legacy_import"];
/**
* The steps of one plan, in the order the engine runs them (WS-19):
*
* 1. `dry_run`: list each source bucket, count its objects and bytes.
* 2. `online_copy`: copy every object to the target while the app runs.
* 3. `pause`: wait for the `pause` approval and for `windowStart`, then freeze the sources.
* 4. `final_copy`: copy the objects that differ, and delete the target objects the source lacks.
* 5. `verify`: compare names, sizes and `crc32c` of both sides.
* 6. `switch`: point the bindings at the targets.
* 7. `hold`: keep the sources read-only for {@link RELOCATION_HOLD_DAYS} days.
* 8. `delete_source`: wait for the `delete_source` approval, then release the sources.
*
* A `legacy_import` plan keeps the eight rows in this order and marks
* {@link LEGACY_IMPORT_SKIPPED_STEPS} `skipped` (R-100 item 4).
*/
const RELOCATION_STEPS = [
	"dry_run",
	"online_copy",
	"pause",
	"final_copy",
	"verify",
	"switch",
	"hold",
	"delete_source"
];
/**
* The states of one plan. `pending` before the first step, `running` while a step runs,
* `waiting_approval` while a gate waits for the platform admin, `aborting` while an abort
* deletes the targets, then one of the three terminal states `aborted`, `failed` and `done`.
*/
const RELOCATION_PLAN_STATES = [
	"pending",
	"running",
	"waiting_approval",
	"aborting",
	"aborted",
	"failed",
	"done"
];
/** The states of one step row. A `cache` bucket plan marks the copy steps `skipped` (R-079 I-7). */
const RELOCATION_STEP_STATES = [
	"pending",
	"running",
	"done",
	"failed",
	"skipped"
];
/**
* The two gates the platform admin approves: before the pause, and before the source delete (R-079
* I-6). A `legacy_import` plan has no pause, so it waits only at `delete_source`.
*/
const RELOCATION_GATES = ["pause", "delete_source"];
/**
* The days a source stays read-only after the switch, before its delete gate opens (R-079 I-5). The
* `hold` step of a `legacy_import` plan keeps the copied source objects for the same 7 days
* (R-100 item 5).
*/
const RELOCATION_HOLD_DAYS = 7;
/** The longest `detail` text of one step row in the plan view. */
const RELOCATION_STEP_DETAIL_MAX_LENGTH = 1024;
/**
* The states a source binding row may hold in an answer of release-engine: `pending` or `ready`
* before the switch, `holding` after it (never projected into the bindings), then `deleting` and
* `deleted` after `release-source`. An adoption removal can also move a source to `deleting`.
*/
const RELOCATION_SOURCE_STATES = [
	"pending",
	"ready",
	"holding",
	"deleting",
	"deleted"
];
/**
* The states a target binding row may hold in an answer of release-engine: `staging` from the stage
* to the switch (never projected into the bindings), `ready` after the switch, and `deleting` then
* `deleted` after an abort. A target reads `holding` after a later plan of the same identity takes
* its row as that plan's source and switches: release-engine answers the row state as it stands, so
* an answer of the earlier plan shows the target in `holding`.
*/
const RELOCATION_TARGET_STATES = [
	"staging",
	"ready",
	"holding",
	"deleting",
	"deleted"
];
/**
* The states that one row of the desired-state member `blobRelocationStates` may hold
* (2026-10-13-blob-buckets R-089 item 3, `00-index.md` stage 4b contracts item 2), in this order:
*
* - `requested`: core-api stored an org admin request, and the CP holds it, but provisioning-svc
*   has not handled it yet. The row has a request id and no relocation id.
* - `refused`: provisioning-svc handled the request and created no plan. The row has a request id,
*   no relocation id and a refusal code.
* - the seven {@link RELOCATION_PLAN_STATES}, in their order: the state of the plan. The row has a
*   relocation id, and a request id when an org admin request created the plan.
*/
const BLOB_RELOCATION_STATE_VALUES = [
	"requested",
	"refused",
	...RELOCATION_PLAN_STATES
];
/**
* The open states of {@link BLOB_RELOCATION_STATE_VALUES}: `requested`, `pending`, `running`,
* `waiting_approval` and `aborting`. An open row can still change. One org and one app have at most
* one open row, because core-api keeps one open request per (org, app) and provisioning-svc keeps
* one open plan per (data plane, org, app).
*/
const BLOB_RELOCATION_OPEN_STATES = [
	"requested",
	"pending",
	"running",
	"waiting_approval",
	"aborting"
];
/**
* The closed states of {@link BLOB_RELOCATION_STATE_VALUES}: `refused`, `aborted`, `failed` and
* `done`. A closed row never changes again. With {@link BLOB_RELOCATION_OPEN_STATES} it partitions
* the list.
*/
const BLOB_RELOCATION_CLOSED_STATES = [
	"refused",
	"aborted",
	"failed",
	"done"
];
/**
* Whether one state of {@link BLOB_RELOCATION_STATE_VALUES} is open.
*
* Who calls it: core-api, for the refusal `relocation_request_open` and for the open requests that
* `GET /internal/v1/blob-relocation-requests` answers; release-engine, for the order of the rows it
* keeps under the cap of the member; the row set check of the member.
*
* Input: one state value. Output: `true` for a member of {@link BLOB_RELOCATION_OPEN_STATES},
* `false` for a member of {@link BLOB_RELOCATION_CLOSED_STATES}. Errors: none. Pure.
*/
function isOpenBlobRelocationState(state) {
	return BLOB_RELOCATION_OPEN_STATES.includes(state);
}
/**
* The days a closed row stays in the desired-state member `blobRelocationStates` after its request
* or plan closed: 30. In that time the operator program puts the row to core-api at least once, so
* core-api learns the end of each request. After it, release-engine leaves the row out. The value
* equals the default removal grace of an org bucket (`removalGraceDays` 30).
*/
const BLOB_RELOCATION_STATES_CLOSED_KEEP_DAYS = 30;
/**
* The grammar of a refusal code in a `refused` row: 1 to 64 characters, a lower-case letter, then
* lower-case letters, digits and `_`. The codes come from provisioning-svc (its
* `RELOCATION_ROUTE_REFUSALS`, for example `relocation_target_not_allowed`, `relocation_org_not_placed`
* or `relocation_plan_open`). The contract fixes the grammar, and provisioning-svc owns the list.
*/
const BLOB_RELOCATION_REFUSAL_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
/**
* The steps a `legacy_import` plan never runs: `pause` and `final_copy`. The app does not stop, so
* no freeze and no final copy run; the `switch` step makes one more diff pass before the switch
* (R-100 item 4). provisioning-svc writes both rows `skipped` at the create, and
* {@link RelocationPlanViewSchema} refuses an import view in which either row holds another state.
*/
const LEGACY_IMPORT_SKIPPED_STEPS = ["pause", "final_copy"];
/**
* The platform prefixes of the shared `app-fixtures` bucket (R-100 item 2): work-run results,
* reporter data, claim-check payloads, mail attachments and the zion-build result index. No app owns
* them, so an import never copies them into an app bucket (D1). {@link LegacyImportCreateBodySchema}
* refuses a prefix that starts with any of them. The schema accepts a prefix that only shares the
* first characters, such as `mailbox/`.
*/
const LEGACY_IMPORT_EXCLUDED_PREFIXES = [
	"workruns/",
	"reporter/",
	"claimcheck/",
	"mail/",
	"gha-res-index/"
];
/**
* The names under a blob-proxy attempt root of the shared `app-fixtures` bucket: two segments, then
* the segment `attempts` and a `/`. The blob proxy writes each work run under the root
* `<orgId>/<appKey>/attempts/<attemptId>/` (`Root` in `infra/docker/blob-proxy/src/keys.rs`). That is
* work-run data of every app of the org, and no app owns it (D1). The pattern matches a name or a
* prefix at or under the `attempts/` level, for example `78945b43-0a1b-4c2d-8e3f-4a5b6c7d8e9f/notes/attempts/`
* and `org/notes/attempts/a1/out.json`. It does not match `org/` or `org/notes/`, which sit above the
* root: a prefix there is valid, and the mover skips each name under it that this pattern matches
* ({@link isLegacyImportExcludedKey}).
*/
const LEGACY_IMPORT_ATTEMPT_ROOT_PATTERN = /^[^/]+\/[^/]+\/attempts\//;
/**
* The key rule of the import mover (WS-25b): true when one object name of the shared bucket must
* never reach an app bucket.
*
* Input: one source object name as the bucket lists it, before the mover strips a leading
* `<orgId>/` (R-100 item 3). Any string.
*
* Output: `true` when the name starts with an entry of {@link LEGACY_IMPORT_EXCLUDED_PREFIXES}
* (R-100 item 2), or when {@link LEGACY_IMPORT_ATTEMPT_ROOT_PATTERN} matches it (a blob-proxy
* attempt object). `false` for every other name. The mover lists each prefix of the import, skips
* every name for which this answers `true`, and copies and verifies the rest. So a prefix
* `<orgId>/` copies the org data of the app and leaves the attempt objects of every app in the
* shared bucket. Pure; never throws.
*/
function isLegacyImportExcludedKey(name) {
	return LEGACY_IMPORT_EXCLUDED_PREFIXES.some((platform) => name.startsWith(platform)) || LEGACY_IMPORT_ATTEMPT_ROOT_PATTERN.test(name);
}
/**
* The name map of the import mover (WS-25b, R-100 item 3): the target name in the app's org bucket
* of one source name of the shared bucket.
*
* Inputs: `name`, one source object name as the bucket lists it (any string); `orgId`, the one
* active org of the data plane, a lower-case UUID.
*
* Output: `name` without its leading `<orgId>/` when it starts with `<orgId>/`, so
* `<orgId>/notes/a.json` maps to `notes/a.json` and the name `<orgId>/` itself maps to the empty
* string. Every other name maps to itself: the mover strips only the active org id, never another
* UUID, so `<other uuid>/summary.json` keeps its name.
*
* Errors: `RangeError` when `orgId` is not a lower-case UUID. Pure.
*/
function legacyImportTargetName(name, orgId) {
	const root = `${legacyImportOrgId(orgId)}/`;
	return name.startsWith(root) ? name.slice(root.length) : name;
}
/**
* The clash check of the import mover (WS-25b): the first target name that two distinct source
* names map onto under {@link legacyImportTargetName}, or `null` when no two names meet.
*
* The check needs the org id and the listing, so {@link legacyImportPrefixesSchema} cannot make it.
* The mover runs it in `dry_run` over every name it lists under the prefixes of the import and keeps
* (each name for which {@link isLegacyImportExcludedKey} answers `false`). A clash means two objects
* would be copied onto one target name, so the mover fails `dry_run` and copies nothing. For
* example `<orgId>/u2/summary.json` and `u2/summary.json` meet at `u2/summary.json`. Two prefixes
* whose names never meet pass, such as `<orgId>/` beside a load-test root `<other uuid>/` whose
* name the org data does not repeat.
*
* Inputs: `names`, the source names in listing order (any strings; a name that occurs twice is one
* object, not a clash); `orgId`, a lower-case UUID.
*
* Output: the clash of the earliest second name in the input order, with both source names in
* input order, or `null`.
*
* Errors: `RangeError` when `orgId` is not a lower-case UUID, before the check reads a name. Pure.
*/
function findLegacyImportNameClash(names, orgId) {
	const org = legacyImportOrgId(orgId);
	const sourceOf = /* @__PURE__ */ new Map();
	for (const name of names) {
		const target = legacyImportTargetName(name, org);
		const first = sourceOf.get(target);
		if (first === void 0) sourceOf.set(target, name);
		else if (first !== name) return {
			target,
			names: [first, name]
		};
	}
	return null;
}
/** `orgId` when it is a lower-case UUID; else a `RangeError`. */
function legacyImportOrgId(orgId) {
	if (!LOWERCASED_UUID_PATTERN.test(orgId)) throw new RangeError(`the org id of a legacy import must be a lower-case UUID: ${orgId}`);
	return orgId;
}
/** The most prefixes one import names: 32. */
const LEGACY_IMPORT_PREFIXES_MAX = 32;
/** The longest prefix, in characters: 256. */
const LEGACY_IMPORT_PREFIX_MAX_LENGTH = 256;
/**
* The grammar of one import prefix: one or more `/`-separated segments and a closing `/`. A segment
* is a letter, a digit, `_` or `-`, then letters, digits, `.`, `_` and `-`. So no segment is empty,
* `.` or `..`, and no segment starts with a dot. Examples: `gha-meta/`, `gha-art/zion-build/`,
* `78945b43-0a1b-4c2d-8e3f-4a5b6c7d8e9f/`.
*/
const LEGACY_IMPORT_PREFIX_PATTERN = /^[A-Za-z0-9_-][A-Za-z0-9._-]*(\/[A-Za-z0-9_-][A-Za-z0-9._-]*)*\/$/;
/**
* The grammar of the shared source bucket of an import: `dp-`, the name of the `DataPlane` XR, then
* `-app-fixtures` and the optional `dpBucketSuffix` of the compositions chart, 3 to 63 characters
* of lower-case letters, digits and `-` that end with a letter or a digit. Examples:
* `dp-dp-app-app-fixtures`, `dp-acme-app-fixtures-x1`. A `zb` physical name never matches it, so an
* import never names an org bucket as its source.
*/
const LEGACY_IMPORT_SOURCE_BUCKET_PATTERN = /^(?=[a-z0-9-]{3,63}$)dp-[a-z0-9-]+-app-fixtures(?:[a-z0-9-]*[a-z0-9])?$/;
/**
* The two states of the hold flag of one (data plane, org, app) in release-engine
* (`release.blob_legacy_import_holds`, R-100 item 4):
*
* - `held`: the row has no `released_at`. release-engine answers the bucket plan `none` for the app
*   on that data plane and org, so the app keeps its legacy store while its org buckets fill.
* - `released`: the import switch set `released_at`. The app reads its org buckets.
*/
const LEGACY_IMPORT_HOLD_STATES = ["held", "released"];
/** The states of a target bucket of an import: a live binding row, `pending` before its XR is ready. */
const LEGACY_IMPORT_TARGET_STATES = ["pending", "ready"];
/** A lower-case UUID: a data plane id, a CP org id or a relocation id. */
const lowerUuidSchema = z.string().regex(LOWERCASED_UUID_PATTERN, "a lower-case UUID");
/** The app slug grammar of the bindings file: 2 to 64 characters of `RESOURCE_SLUG_PATTERN`. */
const appSlugSchema = blobBindingsFileSchema.shape.app;
/**
* The location grammar of an existing bucket: the `location` of `blobBucketPhysicalSchema`, at
* most 31 characters of `BLOB_BUCKET_LOCATION_PATTERN`. A source bucket may sit in a location that
* predates `dataLocationSchema`, so a source location uses this wider grammar.
*/
const sourceLocationSchema = blobBucketPhysicalSchema.options[0].shape.location;
/** One bucket generation: an integer from 1 to `BLOB_BUCKET_GENERATION_MAX`. */
const generationSchema = z.int().min(1).max(BLOB_BUCKET_GENERATION_MAX);
/** One physical bucket name, from `physicalBucketName()` (defaults row F10). */
const physicalNameSchema = z.string().regex(PHYSICAL_BUCKET_NAME_PATTERN, "a physical bucket name from physicalBucketName");
/** The name of the shared source bucket of an import, in {@link LEGACY_IMPORT_SOURCE_BUCKET_PATTERN}. */
const legacySourceBucketSchema = z.string().regex(LEGACY_IMPORT_SOURCE_BUCKET_PATTERN, "the shared app-fixtures bucket of the data plane");
/** One UTC instant in ISO 8601, for example `2026-10-20T02:00:00Z`. No offset other than `Z`. */
const instantSchema = z.iso.datetime();
/** A count that is known or `null`: a non-negative safe integer. */
const countSchema = z.int().min(0).nullable();
/**
* provisioning-svc's route of the plan list: `POST` creates a plan (`cp:dp:update`), and `GET`
* answers {@link RelocationPlanListViewSchema}, the plans of the data plane (`cp:dp:read`, stage 4b,
* R-089 item 4). The controller
* is `v1/data-planes/:dpId/relocations` under `CpJwtAuthGuard` (WS-19). The gateway already routes
* `/v1/data-planes` to provisioning-svc, so the route needs no new HTTPRoute rule.
*/
const RELOCATIONS_ROUTE = "/v1/data-planes/:dpId/relocations";
/** provisioning-svc's route of one plan: `GET` answers its {@link RelocationPlanView} (`cp:dp:read`). */
const RELOCATION_ROUTE = "/v1/data-planes/:dpId/relocations/:relocationId";
/** provisioning-svc's route of a gate approval: `POST` with {@link RelocationApproveBodySchema} (`cp:dp:update`). */
const RELOCATION_APPROVE_ROUTE = "/v1/data-planes/:dpId/relocations/:relocationId/approve";
/** provisioning-svc's route of an abort: `POST` with {@link RelocationAbortBodySchema} (`cp:dp:update`). */
const RELOCATION_ABORT_ROUTE = "/v1/data-planes/:dpId/relocations/:relocationId/abort";
/**
* release-engine's route of one relocation, `GET`, and the base of its five `POST` actions
* ({@link BLOB_RELOCATION_ACTIONS}). The controller is `v1/internal/releases/data-planes` under
* `ProvisioningServiceTokenGuard` (WS-48). provisioning-svc calls it with its CP service token.
*/
const BLOB_RELOCATION_ROUTE = "/v1/internal/releases/data-planes/:dpId/blob-relocations/:relocationId";
/**
* The five `POST` actions under {@link BLOB_RELOCATION_ROUTE}, each a last path segment:
* `stage` with {@link BlobRelocationStageBodySchema}; `freeze`, `switch`, `release-source` and
* `abort` with {@link BlobRelocationActionBodySchema}.
*/
const BLOB_RELOCATION_ACTIONS = [
	"stage",
	"freeze",
	"switch",
	"release-source",
	"abort"
];
/**
* The concrete path of one release-engine relocation route, for provisioning-svc's HTTP client.
*
* Input: a data plane id and a relocation id, each a lower-case UUID, and an action of
* {@link BLOB_RELOCATION_ACTIONS} or `null` for the `GET` route.
*
* Output: {@link BLOB_RELOCATION_ROUTE} with both ids in place, plus `/<action>` for an action. For
* example `/v1/internal/releases/data-planes/<dpId>/blob-relocations/<relocationId>/release-source`.
*
* Failure: a `ZodError` for an id that is not a lower-case UUID (path `['dpId']` or
* `['relocationId']`), and for an action outside the list (path `['action']`). Pure.
*/
function blobRelocationPath(dpId, relocationId, action) {
	const parsed = z.object({
		dpId: lowerUuidSchema,
		relocationId: lowerUuidSchema,
		action: z.enum(BLOB_RELOCATION_ACTIONS).nullable()
	}).strict().parse({
		dpId,
		relocationId,
		action
	});
	const base = BLOB_RELOCATION_ROUTE.replace(":dpId", parsed.dpId).replace(":relocationId", parsed.relocationId);
	return parsed.action === null ? base : `${base}/${parsed.action}`;
}
/**
* release-engine's route of one legacy import, `GET`, and the base of its four `POST` actions
* ({@link BLOB_LEGACY_IMPORT_ACTIONS}) (R-100, WS-25a). The controller is
* `v1/internal/releases/data-planes` under `ProvisioningServiceTokenGuard`, as for
* {@link BLOB_RELOCATION_ROUTE}. provisioning-svc calls it with its CP service token. The
* `relocationId` is the id of the `legacy_import` plan in `provisioning.relocations`.
*/
const BLOB_LEGACY_IMPORT_ROUTE = "/v1/internal/releases/data-planes/:dpId/blob-legacy-imports/:relocationId";
/**
* The four `POST` actions under {@link BLOB_LEGACY_IMPORT_ROUTE}, each a last path segment: `stage`
* with {@link BlobLegacyImportStageBodySchema}; `switch`, `release-source` and `abort` with
* {@link BlobRelocationActionBodySchema}. An import has no `freeze` (R-100 item 4).
*/
const BLOB_LEGACY_IMPORT_ACTIONS = [
	"stage",
	"switch",
	"release-source",
	"abort"
];
/**
* The concrete path of one release-engine legacy import route, for provisioning-svc's HTTP client.
*
* Input: a data plane id and a relocation id, each a lower-case UUID, and an action of
* {@link BLOB_LEGACY_IMPORT_ACTIONS} or `null` for the `GET` route.
*
* Output: {@link BLOB_LEGACY_IMPORT_ROUTE} with both ids in place, plus `/<action>` for an action.
* For example `/v1/internal/releases/data-planes/<dpId>/blob-legacy-imports/<relocationId>/switch`.
*
* Failure: a `ZodError` for an id that is not a lower-case UUID (path `['dpId']` or
* `['relocationId']`), and for an action outside the list, `freeze` included (path `['action']`).
* Pure.
*/
function blobLegacyImportPath(dpId, relocationId, action) {
	const parsed = z.object({
		dpId: lowerUuidSchema,
		relocationId: lowerUuidSchema,
		action: z.enum(BLOB_LEGACY_IMPORT_ACTIONS).nullable()
	}).strict().parse({
		dpId,
		relocationId,
		action
	});
	const base = BLOB_LEGACY_IMPORT_ROUTE.replace(":dpId", parsed.dpId).replace(":relocationId", parsed.relocationId);
	return parsed.action === null ? base : `${base}/${parsed.action}`;
}
/**
* The path parameters of {@link RELOCATIONS_ROUTE}: `{ dpId }`, a lower-case UUID. Strict. A bad
* value is a `ZodError` (the strict pipe answers 400).
*/
const RelocationDataPlaneParamsSchema = z.object({ dpId: lowerUuidSchema }).strict();
/**
* The path parameters of {@link RELOCATION_ROUTE}, {@link RELOCATION_APPROVE_ROUTE},
* {@link RELOCATION_ABORT_ROUTE} and {@link BLOB_RELOCATION_ROUTE}: `{ dpId, relocationId }`, each a
* lower-case UUID. Strict. A bad value is a `ZodError` (the strict pipe answers 400).
*/
const RelocationParamsSchema = z.object({
	dpId: lowerUuidSchema,
	relocationId: lowerUuidSchema
}).strict();
/**
* The body of `POST /v1/data-planes/:dpId/relocations`: a request of the platform admin to move
* every app bucket of one org and one app on the data plane.
*
* - `orgId`: the CP org id, a lower-case UUID.
* - `appSlug`: the app slug, 2 to 64 characters of `RESOURCE_SLUG_PATTERN`. It is required, so a
*   plan never names a platform bucket (R-058).
* - `targetLocation`: a single region in `dataLocationSchema`. provisioning-svc also checks that the
*   data plane's allowed list holds it (R-065); this schema cannot read the list.
* - `windowStart`: optional. The earliest UTC instant of the pause. Absent means "as soon as the
*   `pause` gate is approved".
*
* Refusals, each a `ZodError` (400): an unknown key, a missing member, an upper-case or malformed
* `orgId`, an `appSlug` that is `null` or outside the grammar, a multi-region or malformed
* `targetLocation`, a `windowStart` that is not a UTC ISO 8601 instant. A conforming body parses to
* itself.
*/
const RelocationCreateBodySchema = z.object({
	orgId: lowerUuidSchema,
	appSlug: appSlugSchema,
	targetLocation: dataLocationSchema,
	windowStart: instantSchema.optional()
}).strict();
/**
* The prefix list of a legacy import: 1 to {@link LEGACY_IMPORT_PREFIXES_MAX} (32) prefixes, each
* at most {@link LEGACY_IMPORT_PREFIX_MAX_LENGTH} (256) characters in
* {@link LEGACY_IMPORT_PREFIX_PATTERN}.
*
* Refusals, each a `ZodError` issue at `[index]` of the prefix:
* - a prefix outside the grammar or over 256 characters;
* - a prefix that starts with an entry of {@link LEGACY_IMPORT_EXCLUDED_PREFIXES} (R-100 item 2);
* - a prefix that {@link LEGACY_IMPORT_ATTEMPT_ROOT_PATTERN} matches: it sits under a blob-proxy
*   attempt root, which holds work-run data;
* - a prefix equal to an earlier one ("each prefix once");
* - a prefix that starts with another prefix of the list, in any order ("nested"): the mover would
*   list and copy its objects twice.
*
* An empty list or more than 32 prefixes fails at the list. The order is free. Pure.
*/
const legacyImportPrefixesSchema = z.array(z.string().max(256).regex(LEGACY_IMPORT_PREFIX_PATTERN, "a key prefix of segments that ends with /")).min(1).max(32).superRefine((prefixes, ctx) => {
	prefixes.forEach((prefix, index) => {
		const excluded = LEGACY_IMPORT_EXCLUDED_PREFIXES.find((platform) => prefix.startsWith(platform));
		if (excluded !== void 0) ctx.addIssue({
			code: "custom",
			path: [index],
			message: `the platform prefix ${excluded} belongs to no app`
		});
		if (LEGACY_IMPORT_ATTEMPT_ROOT_PATTERN.test(prefix)) ctx.addIssue({
			code: "custom",
			path: [index],
			message: "the prefix sits under a blob-proxy attempt root, which holds work-run data"
		});
		if (prefixes.indexOf(prefix) < index) {
			ctx.addIssue({
				code: "custom",
				path: [index],
				message: "each prefix once"
			});
			return;
		}
		const parent = prefixes.find((other) => other !== prefix && LEGACY_IMPORT_PREFIX_PATTERN.test(other) && prefix.startsWith(other));
		if (parent !== void 0) ctx.addIssue({
			code: "custom",
			path: [index],
			message: `the prefix sits under the prefix ${parent} of the list`
		});
	});
});
/**
* The body of `POST /v1/data-planes/:dpId/relocations` for a legacy import (R-100):
* `{ kind: 'legacy_import', appSlug, prefixes }`.
*
* - `kind`: the literal `legacy_import`.
* - `appSlug`: the app whose org buckets receive the objects, in the app slug grammar.
* - `prefixes`: {@link legacyImportPrefixesSchema}. The mover lists the shared bucket under each
*   prefix and strips a leading `<orgId>/` from each name (R-100 item 3).
*
* The body names no org and no location. provisioning-svc (WS-25b) takes the one active org of the
* data plane, refuses a data plane with more than one active org and a non-GCP data plane (R-100
* item 1), and copies into the durable org buckets that release-engine answers.
*
* Refusals, each a `ZodError` (400): an unknown key, a missing member, another `kind`, an `appSlug`
* outside the grammar, and each refusal of {@link legacyImportPrefixesSchema} at
* `['prefixes', index]`. A conforming body parses to itself.
*/
const LegacyImportCreateBodySchema = z.object({
	kind: z.literal("legacy_import"),
	appSlug: appSlugSchema,
	prefixes: legacyImportPrefixesSchema
}).strict();
/**
* The body the create route `POST /v1/data-planes/:dpId/relocations` binds: a
* {@link RelocationCreateBodySchema} (a `blob` plan, with no `kind` key) or a
* {@link LegacyImportCreateBodySchema} (with `kind: 'legacy_import'`). The two arms are strict, so a
* body matches at most one: a `kind` key fails the first arm, and its absence fails the second.
* A body that matches neither is a `ZodError` (400). No `.transform()`, so `createOpenApiDocument()`
* represents it as `anyOf`.
*/
const RelocationPlanCreateBodySchema = z.union([RelocationCreateBodySchema, LegacyImportCreateBodySchema]);
/**
* The body of `POST .../relocations/:relocationId/approve`: `{ gate }`, one of
* {@link RELOCATION_GATES}. Strict. An unknown key or gate is a `ZodError` (400). provisioning-svc
* records the approval; the body says nothing about whether the gate waits now.
*/
const RelocationApproveBodySchema = z.object({ gate: z.enum(RELOCATION_GATES) }).strict();
/** The body of `POST .../relocations/:relocationId/abort`: the empty object. Any key is a `ZodError` (400). */
const RelocationAbortBodySchema = z.object({}).strict();
/**
* One step row of the plan view.
*
* - `step`: one of {@link RELOCATION_STEPS}.
* - `state`: one of {@link RELOCATION_STEP_STATES}.
* - `startedAt`, `endedAt`: UTC instants, or `null` before the step starts or ends.
* - `detail`: a short text for the platform admin, at most {@link RELOCATION_STEP_DETAIL_MAX_LENGTH}
*   characters (for example the reason of a `failed` verify), or `null`.
*
* Strict.
*/
const RelocationStepViewSchema = z.object({
	step: z.enum(RELOCATION_STEPS),
	state: z.enum(RELOCATION_STEP_STATES),
	startedAt: instantSchema.nullable(),
	endedAt: instantSchema.nullable(),
	detail: z.string().max(RELOCATION_STEP_DETAIL_MAX_LENGTH).nullable()
}).strict();
/**
* One bucket row of the plan view.
*
* - `logical`: the logical bucket name, in `blobBucketNameSchema`.
* - `durability`: `durable` or `cache`. A `cache` bucket skips the copies (R-079 I-7).
* - `sourcePhysicalName`: a physical name from `physicalBucketName()` for a `blob` plan, or the
*   shared bucket name in {@link LEGACY_IMPORT_SOURCE_BUCKET_PATTERN} for a `legacy_import` plan.
*   {@link RelocationPlanViewSchema} checks which grammar the kind of the plan allows.
* - `targetPhysicalName`: a physical name from `physicalBucketName()`, never equal to the source.
* - `objectCount`, `bytes`: the counts of the source from the dry run, or `null` before it. For an
*   import, the counts of the objects under the prefixes, which the mover copies into each target.
*
* Strict.
*/
const RelocationBucketViewSchema = z.object({
	logical: blobBucketNameSchema,
	durability: z.enum(BLOB_BUCKET_DURABILITIES),
	sourcePhysicalName: z.union([physicalNameSchema, legacySourceBucketSchema]),
	targetPhysicalName: physicalNameSchema,
	objectCount: countSchema,
	bytes: countSchema
}).strict().refine((row) => row.sourcePhysicalName !== row.targetPhysicalName, {
	path: ["targetPhysicalName"],
	message: "the target is another physical bucket than the source"
});
/** Adds an issue at `[index, 'logical']` for each row whose `logical` is not after the row before it. */
function refineAscendingLogical(rows, ctx) {
	rows.forEach((row, index) => {
		const previous = rows[index - 1];
		if (previous !== void 0 && row.logical <= previous.logical) ctx.addIssue({
			code: "custom",
			path: [index, "logical"],
			message: "rows in ascending logical order, each logical once"
		});
	});
}
/**
* The plan view: the answer of `GET /v1/data-planes/:dpId/relocations/:relocationId` and of the
* create, approve and abort routes of provisioning-svc.
*
* - `id`, `dpId`, `orgId`: lower-case UUIDs. `orgId` is the CP org id.
* - `appSlug`, `moverKind` ({@link RELOCATION_MOVER_KINDS}), `targetLocation` (`dataLocationSchema`).
* - `sourceLocation`: the one location of every moved source bucket, or `null` when no single
*   location exists: before the stage answered, or when the moved buckets sit in more than one
*   location (an org admin may pick a location per bucket, R-071).
* - `windowStart`: the UTC instant of the create body, or `null`.
* - `state`: one of {@link RELOCATION_PLAN_STATES}.
* - `steps`: exactly one row per step of {@link RELOCATION_STEPS}, in that order.
* - `buckets`: 0 to `BLOB_BUCKETS_PER_APP_MAX` (16) rows in ascending `logical` order, each logical
*   once. The list is empty until the stage answered.
*
* A `legacy_import` plan (R-100) reads as follows:
* - `orgId`: the one active org of the data plane at the create.
* - `sourceLocation`: the location of the shared bucket, or `null` before the stage answered.
* - `targetLocation`: the home region of the data plane (`platform.data_placements.region`), the
*   default org data location (R-065). The targets keep the location of each org bucket row; the
*   view does not show it.
* - `windowStart`: always `null`, because the create body has no window.
* - `steps`: {@link LEGACY_IMPORT_SKIPPED_STEPS} read `skipped`.
* - `buckets`: one row per durable target, each with the shared bucket as its source.
*
* Refusals, each a `ZodError`: an unknown key at any depth; a member outside its grammar; a step
* list of another length (path `['steps']`) or another order (path `['steps', index, 'step']`); a
* bucket list over 16 rows, or out of order (path `['buckets', index, 'logical']`). By the kind:
* - `blob`: a source that is not a `physicalBucketName()` name (path
*   `['buckets', index, 'sourcePhysicalName']`).
* - `legacy_import`: a skipped step in another state (path `['steps', index, 'state']`); a
*   `windowStart` other than `null` (path `['windowStart']`); a source that is not a shared bucket
*   name, or that differs from the first row's source (path
*   `['buckets', index, 'sourcePhysicalName']`); a `cache` row (path `['buckets', index, 'durability']`);
*   a row whose logical name is `claimcheck`, the platform claim-check bucket (path
*   `['buckets', index, 'logical']`).
*
* A conforming view parses to itself. Pure.
*/
const RelocationPlanViewSchema = z.object({
	id: lowerUuidSchema,
	dpId: lowerUuidSchema,
	orgId: lowerUuidSchema,
	appSlug: appSlugSchema,
	moverKind: z.enum(RELOCATION_MOVER_KINDS),
	sourceLocation: sourceLocationSchema.nullable(),
	targetLocation: dataLocationSchema,
	windowStart: instantSchema.nullable(),
	state: z.enum(RELOCATION_PLAN_STATES),
	steps: z.array(RelocationStepViewSchema).length(RELOCATION_STEPS.length).superRefine((rows, ctx) => {
		if (rows.length !== RELOCATION_STEPS.length) return;
		rows.forEach((row, index) => {
			if (row.step !== RELOCATION_STEPS[index]) ctx.addIssue({
				code: "custom",
				path: [index, "step"],
				message: `step ${index + 1} is ${RELOCATION_STEPS[index]}`
			});
		});
	}),
	buckets: z.array(RelocationBucketViewSchema).max(BLOB_BUCKETS_PER_APP_MAX).superRefine(refineAscendingLogical)
}).strict().superRefine((view, ctx) => {
	if (view.moverKind === "blob") {
		view.buckets.forEach((row, index) => {
			if (!PHYSICAL_BUCKET_NAME_PATTERN.test(row.sourcePhysicalName)) ctx.addIssue({
				code: "custom",
				path: [
					"buckets",
					index,
					"sourcePhysicalName"
				],
				message: "a blob plan moves a physical bucket"
			});
		});
		return;
	}
	view.steps.forEach((row, index) => {
		if (LEGACY_IMPORT_SKIPPED_STEPS.includes(row.step) && row.state !== "skipped") ctx.addIssue({
			code: "custom",
			path: [
				"steps",
				index,
				"state"
			],
			message: `a legacy import skips the ${row.step} step`
		});
	});
	if (view.windowStart !== null) ctx.addIssue({
		code: "custom",
		path: ["windowStart"],
		message: "a legacy import has no window"
	});
	const source = view.buckets[0]?.sourcePhysicalName;
	view.buckets.forEach((row, index) => {
		if (!LEGACY_IMPORT_SOURCE_BUCKET_PATTERN.test(row.sourcePhysicalName) || row.sourcePhysicalName !== source) ctx.addIssue({
			code: "custom",
			path: [
				"buckets",
				index,
				"sourcePhysicalName"
			],
			message: "every row of a legacy import reads the one shared bucket"
		});
		if (row.durability !== "durable") ctx.addIssue({
			code: "custom",
			path: [
				"buckets",
				index,
				"durability"
			],
			message: "a legacy import copies into durable buckets only"
		});
		if (row.logical === BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME) ctx.addIssue({
			code: "custom",
			path: [
				"buckets",
				index,
				"logical"
			],
			message: "the platform claimcheck bucket is never an import target"
		});
	});
});
/** The most plan views one answer of `GET /v1/data-planes/:dpId/relocations` carries: 100. */
const RELOCATION_PLAN_LIST_MAX = 100;
/**
* The 200 body of `GET /v1/data-planes/:dpId/relocations` (2026-10-13-blob-buckets R-089 item 4,
* `00-index.md` stage 4b contracts item 5): `{ relocations }`, the plan views of the data plane.
*
* Producer: provisioning-svc (WS-52), under `cp:dp:read`. It answers every plan of the data plane,
* open and closed, newest first: `created_at` descending, then `id` descending in byte order for one
* instant. It answers the first {@link RELOCATION_PLAN_LIST_MAX} (100) plans and leaves out the
* rest. A data plane with no plan answers `{ relocations: [] }`. The view carries no instant, so the
* schema cannot check the order; the order is the producer's rule. Consumer: the console (WS-53).
*
* Refusals, each a `ZodError`: an unknown key at any depth; a view that fails
* {@link RelocationPlanViewSchema}, at `['relocations', index, ...]`; more than 100 views, at
* `['relocations']`; an `id` that an earlier view holds, at `['relocations', index, 'id']`; a `dpId`
* other than the first view's, at `['relocations', index, 'dpId']`. A conforming body parses to
* itself. Pure.
*/
const RelocationPlanListViewSchema = z.object({ relocations: z.array(RelocationPlanViewSchema).max(100).superRefine((views, ctx) => {
	const seen = /* @__PURE__ */ new Set();
	let dpId;
	views.forEach((view, index) => {
		if (seen.has(view.id)) ctx.addIssue({
			code: "custom",
			path: [index, "id"],
			message: "each plan appears once"
		});
		seen.add(view.id);
		dpId ??= view.dpId;
		if (view.dpId !== dpId) ctx.addIssue({
			code: "custom",
			path: [index, "dpId"],
			message: "every plan is on the one data plane of the route"
		});
	});
}) }).strict();
/**
* The body of `POST .../blob-relocations/:relocationId/stage`: `{ orgId, appSlug, targetLocation }`,
* with the grammars of {@link RelocationCreateBodySchema}. Strict. Refusals are `ZodError`s (400).
*/
const BlobRelocationStageBodySchema = z.object({
	orgId: lowerUuidSchema,
	appSlug: appSlugSchema,
	targetLocation: dataLocationSchema
}).strict();
/** The body of `freeze`, `switch`, `release-source` and `abort`: the empty object. Any key is a `ZodError` (400). */
const BlobRelocationActionBodySchema = z.object({}).strict();
/**
* The states in which a source can read `frozen: true`: the states whose `BlobBucket` XR exists
* and serves reads. A `deleting` or `deleted` source always reads `frozen: false`.
*/
const RELOCATION_FREEZABLE_SOURCE_STATES = [
	"pending",
	"ready",
	"holding"
];
/**
* The source half of one {@link RelocationBucket}: the binding row the app uses before the switch.
*
* - `physicalName`, `location` (the existing bucket grammar), `generation`.
* - `state`: one of {@link RELOCATION_SOURCE_STATES}.
* - `frozen`: the read-back of the write freeze (R-082 item 1). `true` when the row holds the
*   freeze of a `freeze` call, the spec of the source XR holds the freeze (every app member
*   `objectViewer`), and the XR reads `Synced` and `Ready` at a generation at or after the freeze
*   write. A row flag alone gives `false`. `false` before a freeze, after an abort, and for a
*   source in `deleting` or `deleted`. A `cache` source reads the same way; the switch and the
*   engine check only a `durable` source (R-079 I-7, R-082 item 2).
*
* Refusals, each a `ZodError`: an unknown key; a member outside its grammar; `frozen: true` on a
* source in `deleting` or `deleted` (path `['frozen']`). Strict.
*/
const RelocationBucketSourceSchema = z.object({
	physicalName: physicalNameSchema,
	location: sourceLocationSchema,
	generation: generationSchema,
	state: z.enum(RELOCATION_SOURCE_STATES),
	frozen: z.boolean()
}).strict().superRefine((source, ctx) => {
	if (source.frozen && !RELOCATION_FREEZABLE_SOURCE_STATES.includes(source.state)) ctx.addIssue({
		code: "custom",
		path: ["frozen"],
		message: "only a pending, ready or holding source reads frozen"
	});
});
/**
* The target half of one {@link RelocationBucket}: the binding row the stage added. `physicalName`,
* `location` (`dataLocationSchema`), `generation`, `state` in {@link RELOCATION_TARGET_STATES}, and
* `ready`: `true` when the `BlobBucket` XR of the target reports ready and the row is `staging` or
* `ready`; `false` otherwise. Strict.
*/
const RelocationBucketTargetSchema = z.object({
	physicalName: physicalNameSchema,
	location: dataLocationSchema,
	generation: generationSchema,
	state: z.enum(RELOCATION_TARGET_STATES),
	ready: z.boolean()
}).strict();
/**
* One bucket of a relocation, as release-engine answers it: `{ logical, durability, source, target }`.
*
* Invariants, each a refusal of the schema:
* - `target.generation` is greater than `source.generation` (path `['target', 'generation']`): the
*   stage gives the target `1 + max` over every row of the identity, so its physical name differs.
* - `target.location` differs from `source.location` (path `['target', 'location']`): the stage
*   leaves out a source that already sits at the target location.
* - `target.ready` is `false` when `target.state` is `holding`, `deleting` or `deleted` (path
*   `['target', 'ready']`): only a `staging` or a `ready` target is ready.
*
* Strict at every depth. A conforming bucket parses to itself.
*/
const RelocationBucketSchema = z.object({
	logical: blobBucketNameSchema,
	durability: z.enum(BLOB_BUCKET_DURABILITIES),
	source: RelocationBucketSourceSchema,
	target: RelocationBucketTargetSchema
}).strict().superRefine((bucket, ctx) => {
	if (bucket.target.generation <= bucket.source.generation) ctx.addIssue({
		code: "custom",
		path: ["target", "generation"],
		message: "the target generation is above the source generation"
	});
	if (bucket.target.location === bucket.source.location) ctx.addIssue({
		code: "custom",
		path: ["target", "location"],
		message: "the target sits in another location than the source"
	});
	if (bucket.target.ready && bucket.target.state !== "staging" && bucket.target.state !== "ready") ctx.addIssue({
		code: "custom",
		path: ["target", "ready"],
		message: "only a staging or a ready target is ready"
	});
});
/**
* The 200 body of every release-engine relocation route: `{ buckets }`, 1 to
* `BLOB_BUCKETS_PER_APP_MAX` (16) {@link RelocationBucket} rows in ascending `logical` order, each
* logical once, and every `target.location` equal (the stage target).
*
* Refusals, each a `ZodError`: an unknown key; an empty list or more than 16 rows (path
* `['buckets']`); a bad row; a row out of order (path `['buckets', index, 'logical']`); a target
* location that differs from the first row's (path `['buckets', index, 'target', 'location']`).
* A conforming body parses to itself. Pure.
*/
const BlobRelocationAnswerSchema = z.object({ buckets: z.array(RelocationBucketSchema).min(1).max(BLOB_BUCKETS_PER_APP_MAX).superRefine(refineAscendingLogical).superRefine((rows, ctx) => {
	const first = rows[0];
	rows.forEach((row, index) => {
		if (first !== void 0 && row.target.location !== first.target.location) ctx.addIssue({
			code: "custom",
			path: [
				index,
				"target",
				"location"
			],
			message: "every target sits in the one stage location"
		});
	});
}) }).strict();
/**
* The body of `POST .../blob-legacy-imports/:relocationId/stage`: `{ orgId, appSlug }`, the one
* active org of the data plane (a lower-case CP org id) and the app slug. No location: the targets
* are the app's own durable org buckets wherever they sit. Strict. Refusals are `ZodError`s (400).
*/
const BlobLegacyImportStageBodySchema = z.object({
	orgId: lowerUuidSchema,
	appSlug: appSlugSchema
}).strict();
/**
* The shared source bucket of an import: `{ bucket, location }`. `bucket` is the `app-fixtures`
* bucket of the data plane, in {@link LEGACY_IMPORT_SOURCE_BUCKET_PATTERN}; `location` is its
* location, in the existing bucket grammar. Strict.
*/
const LegacyImportSourceSchema = z.object({
	bucket: legacySourceBucketSchema,
	location: sourceLocationSchema
}).strict();
/**
* One target bucket of an import: a live `durable` binding row of the app for the org, which the
* release-engine reconciler serves. `{ logical, physicalName, location, generation, state }`, with
* `state` in {@link LEGACY_IMPORT_TARGET_STATES}: `pending` until the allocator saw its XR ready,
* then `ready`. `location` is in the existing bucket grammar. Strict.
*/
const LegacyImportTargetSchema = z.object({
	logical: blobBucketNameSchema.refine((logical) => logical !== BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME, { message: "the platform claimcheck bucket is never an import target" }),
	physicalName: physicalNameSchema,
	location: sourceLocationSchema,
	generation: generationSchema,
	state: z.enum(LEGACY_IMPORT_TARGET_STATES)
}).strict();
/**
* One name of {@link BlobLegacyImportAnswer}'s `unallocated` list: a logical bucket name in the
* existing grammar, never the platform `claimcheck` bucket.
*/
const unallocatedNameSchema = blobBucketNameSchema.refine((logical) => logical !== BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME, { message: "the platform claimcheck bucket is never an import target" });
/** Refuses a name of the list that is not above the name before it: ascending order, each name once. */
function refineAscendingNames(names, ctx) {
	names.forEach((name, index) => {
		const previous = names[index - 1];
		if (previous !== void 0 && name <= previous) ctx.addIssue({
			code: "custom",
			path: [index],
			message: "names in ascending order, each name once"
		});
	});
}
/**
* The 200 body of every release-engine legacy import route: `{ source, hold, targets, unallocated }`.
*
* - `source`: the shared bucket ({@link LegacyImportSourceSchema}).
* - `hold`: the hold flag of the (data plane, org, app), {@link LEGACY_IMPORT_HOLD_STATES}.
* - `targets`: 0 to `BLOB_BUCKETS_PER_APP_MAX` (16) {@link LegacyImportTarget} rows in ascending
*   `logical` order, each logical once. The rows are the live `durable` rows at the time of the
*   answer, so the list grows while the allocator makes the org buckets of the app. An empty list
*   means the allocator has made none yet: the app is not in `BLOB_BUCKET_APP_SLUGS`, or its rows
*   are not written. A `cache` bucket is never a target.
* - `unallocated` (R-111 item 1): the logical names of the declared `durable` buckets of the app
*   that have no live row for the org at the time of the answer, 0 to `BLOB_BUCKETS_PER_APP_MAX`
*   (16) names in ascending order, each name once, never `claimcheck`. The declared set is the
*   switch rule of R-107 item 9: the current manifest of the app or any active version. A name
*   leaves the list when the allocator writes its row, and then shows in `targets`. So a name is
*   never in both lists. provisioning-svc waits while the list is not empty: `online_copy` does not
*   start, and the switch does not run (R-111 item 3).
*
* Refusals, each a `ZodError`: an unknown key at any depth; a member outside its grammar; more than
* 16 targets (path `['targets']`); a target out of order (path `['targets', index, 'logical']`); a
* body with no `unallocated` (path `['unallocated']`); more than 16 names (path `['unallocated']`); a
* name out of order or repeated (path `['unallocated', index]`); `claimcheck` as a name (path
* `['unallocated', index]`); a name that is also the `logical` of a target (path
* `['unallocated', index]`, message `a name with a live row is a target`). A conforming body parses
* to itself. Pure.
*/
const BlobLegacyImportAnswerSchema = z.object({
	source: LegacyImportSourceSchema,
	hold: z.enum(LEGACY_IMPORT_HOLD_STATES),
	targets: z.array(LegacyImportTargetSchema).max(BLOB_BUCKETS_PER_APP_MAX).superRefine(refineAscendingLogical),
	unallocated: z.array(unallocatedNameSchema).max(BLOB_BUCKETS_PER_APP_MAX).superRefine(refineAscendingNames)
}).strict().superRefine((answer, ctx) => {
	const allocated = new Set(answer.targets.map((target) => target.logical));
	answer.unallocated.forEach((name, index) => {
		if (allocated.has(name)) ctx.addIssue({
			code: "custom",
			path: ["unallocated", index],
			message: "a name with a live row is a target"
		});
	});
});
/**
* The coded refusals of the release-engine relocation routes. release-engine answers each with a
* 4xx body `{ error: <code>, message }`. Each route is idempotent: a repeat of an action that took
* effect answers the rows again, with no refusal.
*
* - `relocation_nothing_to_move` (409, `stage`): the (data plane, org, app) has no app bucket row in
*   `pending` or `ready`, or the data plane is unknown.
* - `relocation_same_location` (409, `stage`): every such row already sits at the target location.
* - `relocation_provider_unsupported` (409, `stage`): the data plane is not `gcp` (R-079 I-4).
* - `relocation_mover_unconfigured` (409, `stage`): `BLOB_RELOCATION_MOVER_MEMBER` is unset.
* - `relocation_already_switched` (409, `abort`): the switch ran; a rollback is a new plan back.
*   provisioning-svc reads it as a done switch (R-083 item 3).
* - `relocation_not_frozen` (409, `switch`): no `freeze` ran, or a `durable` source in `pending` or
*   `ready` does not read `frozen: true` (R-082 item 3). A `cache` source never causes it.
* - `relocation_not_found` (404, every route but `stage`): release-engine holds no relocation with
*   this id on this data plane, or holds only the abort tombstone of the id. An `abort` of an
*   unknown id writes that tombstone, an `aborted` relocation with no buckets, and still answers
*   this code; a repeat `abort` answers it again (R-083 item 4).
* - `relocation_conflict` (409, `stage`): this relocation id was staged with another org, app or
*   target location, or another relocation holds a `staging` row of the same org and app.
* - `relocation_targets_not_ready` (409, `switch`): a target row is not `staging` with a ready XR.
* - `relocation_not_switched` (409, `release-source`): the switch has not run.
* - `relocation_aborted` (409, `stage`, `freeze`, `switch`, `release-source`): the relocation was
*   aborted; a new plan takes a new id. A `stage` of an id that holds the abort tombstone answers
*   this code and writes no row (R-083 item 4).
* - `relocation_import_bound` (409, import `stage`): the app already reads its org buckets for this
*   org: its hold row is `released`, or it has no hold row and a served binding row of the org is
*   `ready`. A hold now would move the app back to the shared bucket, so release-engine writes no
*   hold (R-100 item 1: one import per app).
*
* The first six come from `00-index.md` (stage 4a contracts item 1). The next five close the cases
* the index leaves open, each a refusal over an accept (defaults row F3); see escalation E-1 of
* `contracts-WS-19.md`. The last one is the legacy import's (stage 6, `contracts-WS-25a.md`).
*
* The legacy import routes of {@link BLOB_LEGACY_IMPORT_ROUTE} answer the same codes with the same
* statuses, as {@link ReleaseEngineLegacyImportClient} lists them per method. The relocation routes
* answer `relocation_not_found` for the id of an import, and the import routes answer it for the
* id of a `blob` relocation.
*/
const BLOB_RELOCATION_ERRORS = [
	"relocation_nothing_to_move",
	"relocation_same_location",
	"relocation_provider_unsupported",
	"relocation_mover_unconfigured",
	"relocation_already_switched",
	"relocation_not_frozen",
	"relocation_not_found",
	"relocation_conflict",
	"relocation_targets_not_ready",
	"relocation_not_switched",
	"relocation_aborted",
	"relocation_import_bound"
];
/** The HTTP status of each coded refusal. */
const BLOB_RELOCATION_ERROR_STATUS = Object.freeze({
	relocation_nothing_to_move: 409,
	relocation_same_location: 409,
	relocation_provider_unsupported: 409,
	relocation_mover_unconfigured: 409,
	relocation_already_switched: 409,
	relocation_not_frozen: 409,
	relocation_not_found: 404,
	relocation_conflict: 409,
	relocation_targets_not_ready: 409,
	relocation_not_switched: 409,
	relocation_aborted: 409,
	relocation_import_bound: 409
});
/**
* The coded refusal of one release-engine answer, for provisioning-svc's HTTP client.
*
* Input: the HTTP status and the parsed JSON body of any answer.
*
* Output: the code when the body is an object whose `error` is one of {@link BLOB_RELOCATION_ERRORS}
* and `status` is the status {@link BLOB_RELOCATION_ERROR_STATUS} gives that code; `null` for every
* other answer: a success, a 400 of the validation pipe, a 401 or 403 of the guard, a 5xx, a body
* that is not an object, an inherited key such as `toString`, and a code with the wrong status. A
* caller treats `null` on a non-2xx answer as an uncoded failure. Never throws. Pure.
*/
function blobRelocationRefusalOf(status, body) {
	if (typeof body !== "object" || body === null) return null;
	const { error } = body;
	if (typeof error !== "string" || !BLOB_RELOCATION_ERRORS.includes(error)) return null;
	const code = error;
	return BLOB_RELOCATION_ERROR_STATUS[code] === status ? code : null;
}
/**
* The error a {@link ReleaseEngineRelocationClient} throws for a coded refusal. `code` is the code,
* `status` its HTTP status, and `message` names the code and nothing else.
*/
var BlobRelocationRefusal = class extends Error {
	code;
	status;
	constructor(code) {
		super(`blob relocation refused: ${code}`);
		this.name = "BlobRelocationRefusal";
		this.code = code;
		this.status = BLOB_RELOCATION_ERROR_STATUS[code];
	}
};
const seedSchema = z.object({
	dpId: lowerUuidSchema,
	orgId: lowerUuidSchema,
	appSlug: appSlugSchema,
	logical: blobBucketNameSchema,
	durability: z.enum(BLOB_BUCKET_DURABILITIES),
	location: sourceLocationSchema,
	generation: generationSchema.optional(),
	state: z.enum([
		"pending",
		"ready",
		"deleting",
		"deleted"
	]).optional(),
	served: z.boolean().optional()
}).strict();
/** The order of {@link FakeReleaseEngineRelocationClient.bindings}: org, app, logical, then generation. */
function compareRows(a, b) {
	for (const field of [
		"orgId",
		"appSlug",
		"logical"
	]) if (a[field] !== b[field]) return a[field] < b[field] ? -1 : 1;
	return a.generation - b.generation;
}
/** The key of one hold row: data plane, org and app. */
function holdKey(dpId, orgId, appSlug) {
	return `${dpId}/${orgId}/${appSlug}`;
}
/** True for an open legacy import: staged or switched, not released and not aborted. */
function isOpenImport(relocation) {
	return relocation.kind === "legacy_import" && (relocation.phase === "staged" || relocation.phase === "switched");
}
/** True for a row in `pending` or `ready`: the rows the app reads and a stage moves. */
function isLiveSource(row) {
	return row.state === "pending" || row.state === "ready";
}
/**
* The complete in-memory twin of the six release-engine relocation routes (defaults row F9).
*
* It holds the data planes, the app bucket rows and the relocations, and it answers every method of
* {@link ReleaseEngineRelocationClient} with the rows, the state moves and the coded refusals that
* release-engine answers for the same state (WS-48). It parses each input with the route's schema,
* so an input the route answers with 400 throws the same `ZodError`. It parses each answer with
* {@link BlobRelocationAnswerSchema} before it returns it. Every physical name comes from
* `physicalBucketName()` (defaults row F10).
*
* Five hooks stand in for the release-engine reconciler, its allocator and its deployment:
* - {@link markTargetReady}: the `BlobBucket` XR of a target reports ready.
* - {@link applyFreeze}: the reconciler applied the write freeze to each frozen source XR, so the
*   answer reads `source.frozen: true` (R-082 item 1, R-085 item 1).
* - {@link markRowDeleting}: the allocator moves a source to `deleting` after an adoption removal.
* - {@link completeDeletes}: the reconciler deleted every XR of a `deleting` row.
* - {@link setMoverConfigured}: an operator sets or clears `BLOB_RELOCATION_MOVER_MEMBER`.
*
* The seed option `served: false` stands for a row the reconciler does not serve; a `stage` skips
* it (R-085 item 1).
*
* The legacy import (R-100): {@link seedDataPlane} takes the shared bucket of the data plane,
* {@link markRowReady} stands for the allocator that saw the XR of a `pending` row ready, and
* {@link importHold} reads the hold row of one (data plane, org, app). One relocation id names one
* plan of one kind: each family of methods answers `relocation_not_found` for an id of the other
* kind, and both share the abort tombstone.
*
* The checks of `stage`, in order: a known relocation id (aborted or tombstone, then another body,
* else the rows again), the mover, the provider of a known data plane, the served rows to move, the
* location, another relocation's `staging` row, and an open legacy import of the same (data plane,
* org, app) (`relocation_conflict`).
*
* The checks of `switch` in phase `frozen`, in order: each `durable` source in `pending` or `ready`
* reads `frozen: true` (else `relocation_not_frozen`), then each target is ready (else
* `relocation_targets_not_ready`).
*/
var FakeReleaseEngineRelocationClient = class {
	#providers = /* @__PURE__ */ new Map();
	#rows = [];
	#relocations = /* @__PURE__ */ new Map();
	#sources = /* @__PURE__ */ new Map();
	#holds = /* @__PURE__ */ new Map();
	/** The app slugs that `release.apps` does not hold ({@link setAppKnown}). Every other slug is known. */
	#unknownApps = /* @__PURE__ */ new Set();
	/** The CP org ids that `platform.organizations` does not hold ({@link setOrgKnown}). Every other id is known. */
	#unknownOrgs = /* @__PURE__ */ new Set();
	/** The served buckets of each (data plane, app), keyed `<dpId>/<appSlug>`: the logical name and its durability. */
	#declared = /* @__PURE__ */ new Map();
	#moverConfigured;
	/** A Fake with no data plane, no row and no relocation. */
	constructor(options = {}) {
		this.#moverConfigured = options.moverConfigured ?? true;
	}
	/**
	* Registers the data plane `dpId` with its `provider` (`gcp`, `local` or `aws`), or changes the
	* provider of a known one. `legacySource`, when given, is the shared `app-fixtures` bucket of the
	* data plane, the source of a legacy import; when absent, a known shared bucket stays as it is,
	* and a new data plane has none. Throws `ZodError` for an id that is not a lower-case UUID and for
	* a `legacySource` outside {@link LegacyImportSourceSchema}, and `RangeError` for a provider
	* outside `DP_PROVIDERS`.
	*/
	seedDataPlane(dpId, provider, legacySource) {
		const id = lowerUuidSchema.parse(dpId);
		if (!DP_PROVIDERS.includes(provider)) throw new RangeError(`${String(provider)} is not a data plane provider`);
		const source = legacySource === void 0 ? void 0 : LegacyImportSourceSchema.parse(legacySource);
		this.#providers.set(id, provider);
		if (source !== void 0) this.#sources.set(id, source);
	}
	/**
	* Adds one app bucket row (see {@link FakeRelocationBucketSeed}) and answers its physical name.
	* Throws `ZodError` for a seed outside its grammar, and `RangeError` when the data plane is not
	* seeded, when the generation is already taken for the identity, or when a second `pending` or
	* `ready` row would join the identity (the live index of release-engine).
	*/
	seedBucket(seed) {
		const parsed = seedSchema.parse(seed);
		if (!this.#providers.has(parsed.dpId)) throw new RangeError(`no data plane ${parsed.dpId} is seeded`);
		const identity = this.#identityRows(parsed.dpId, parsed.orgId, parsed.appSlug, parsed.logical);
		const generation = parsed.generation ?? this.#nextGeneration(identity);
		if (identity.some((row) => row.generation === generation)) throw new RangeError(`generation ${generation} of ${parsed.logical} already exists`);
		const state = parsed.state ?? "ready";
		if ((state === "pending" || state === "ready") && identity.some(isLiveSource)) throw new RangeError(`${parsed.logical} already has a pending or ready row`);
		const row = this.#addRow({
			dpId: parsed.dpId,
			orgId: parsed.orgId,
			appSlug: parsed.appSlug,
			logical: parsed.logical,
			durability: parsed.durability,
			location: parsed.location,
			generation,
			state,
			served: parsed.served ?? true
		});
		row.xrReady = state === "ready";
		this.#unknownOrgs.delete(parsed.orgId);
		this.#unknownApps.delete(parsed.appSlug);
		if (row.served) this.#declare(parsed.dpId, parsed.appSlug, parsed.logical, parsed.durability);
		return row.physicalName;
	}
	/**
	* The app `appSlug` declares the bucket `logical` with `durability` on the data plane `dpId`, and
	* the reconciler serves it, while the allocator has made no row of it for some org yet: it waits
	* for the settings row of the org, or the org is over its byte bound. A `seedBucket` with
	* `served` not `false` declares its bucket the same way. A second declaration of a name replaces
	* its durability. The reserved `claimcheck` bucket is never an import target, so its declaration
	* changes no import answer (R-104 item 3).
	*
	* Effect: every import answer of the app names a declared `durable` bucket other than
	* `claimcheck` in `unallocated` while it has no target row of the import's org (R-111 item 2),
	* and `switchImport` refuses `relocation_targets_not_ready` while that list is not empty
	* (finding WS-25a-R6). Throws
	* `ZodError` for an argument outside its grammar, and `RangeError` when the data plane is not
	* seeded.
	*/
	declareBucket(dpId, appSlug, logical, durability) {
		const parsed = seedSchema.pick({
			dpId: true,
			appSlug: true,
			logical: true,
			durability: true
		}).parse({
			dpId,
			appSlug,
			logical,
			durability
		});
		if (!this.#providers.has(parsed.dpId)) throw new RangeError(`no data plane ${parsed.dpId} is seeded`);
		this.#declare(parsed.dpId, parsed.appSlug, parsed.logical, parsed.durability);
	}
	/**
	* Adds or removes the app `appSlug` in the release-engine catalog (`release.apps`). Every app is
	* known until this hook removes it, and `seedBucket` makes the app of its row known again.
	*
	* Effect of a removal: the hold rows of the app go, as the foreign key cascade of release-engine
	* removes them, so every import route of an import of the app answers `relocation_not_found`;
	* a later `stageImport` of the app answers `relocation_nothing_to_move` (R-107 item 7). A later
	* re-add finds no hold. Throws `ZodError` for a slug outside the app slug grammar.
	*/
	setAppKnown(appSlug, known) {
		const slug = seedSchema.shape.appSlug.parse(appSlug);
		if (known) {
			this.#unknownApps.delete(slug);
			return;
		}
		this.#unknownApps.add(slug);
		for (const key of this.#holds.keys()) if (key.split("/")[2] === slug) this.#holds.delete(key);
	}
	/**
	* Adds or removes the CP org `orgId` in `platform.organizations`. Every org is known until this
	* hook removes it, and `seedBucket` makes the org of its row known again.
	*
	* Effect of a removal: the hold rows of the org go on every data plane, as the foreign key cascade
	* of release-engine removes them, so every import route of an import of the org answers
	* `relocation_not_found`; a later `stageImport` of the org answers `relocation_nothing_to_move`
	* (R-107 item 7). A later re-add finds no hold. Throws `ZodError` for an id that is not a
	* lower-case UUID.
	*/
	setOrgKnown(orgId, known) {
		const id = seedSchema.shape.orgId.parse(orgId);
		if (known) {
			this.#unknownOrgs.delete(id);
			return;
		}
		this.#unknownOrgs.add(id);
		for (const key of this.#holds.keys()) if (key.split("/")[1] === id) this.#holds.delete(key);
	}
	/**
	* The XR of each `staging` target of the relocation reports ready; with `logical`, only the target
	* of that bucket. Throws `RangeError` for an unknown relocation or an abort tombstone, and for a
	* `logical` that the relocation does not move.
	*/
	markTargetReady(dpId, relocationId, logical) {
		const pairs = this.#held(dpId, relocationId).pairs.filter((pair) => logical === void 0 || pair.target.logical === logical);
		if (pairs.length === 0) throw new RangeError(`relocation ${relocationId} moves no bucket ${String(logical)}`);
		for (const { target } of pairs) if (target.state === "staging") target.xrReady = true;
	}
	/**
	* The reconciler applied the write freeze to the XR of each source of the relocation that holds
	* the freeze flag, `durable` and `cache` alike, so each such source answers `frozen: true` while it
	* is `pending`, `ready` or `holding`. A source with no flag (no `freeze` ran, or an `abort` lifted
	* it) stays as it is. Throws `RangeError` for an unknown relocation or an abort tombstone.
	*/
	applyFreeze(dpId, relocationId) {
		for (const { source } of this.#held(dpId, relocationId).pairs) if (source.frozen) source.freezeApplied = true;
	}
	/**
	* The allocator moved the `pending` or `ready` row named `physicalName` to `deleting`, as an
	* adoption removal does. A relocation that moves the row keeps its target; a later `switch` leaves
	* the row `deleting`, and `releaseSource` leaves it as it is. Throws `RangeError` when the data
	* plane holds no such row, or when the row is in another state.
	*/
	markRowDeleting(dpId, physicalName) {
		const row = this.#rows.find((candidate) => candidate.dpId === dpId && candidate.physicalName === physicalName);
		if (row === void 0) throw new RangeError(`no row ${physicalName} on ${dpId}`);
		if (!isLiveSource(row)) throw new RangeError(`row ${physicalName} is ${row.state}, not pending or ready`);
		row.state = "deleting";
		row.xrReady = false;
	}
	/**
	* The allocator saw the XR of the `pending` row named `physicalName` ready: the row moves to
	* `ready`. Throws `RangeError` when the data plane holds no such row, or when the row is in
	* another state.
	*/
	markRowReady(dpId, physicalName) {
		const row = this.#rows.find((candidate) => candidate.dpId === dpId && candidate.physicalName === physicalName);
		if (row === void 0) throw new RangeError(`no row ${physicalName} on ${dpId}`);
		if (row.state !== "pending") throw new RangeError(`row ${physicalName} is ${row.state}, not pending`);
		row.state = "ready";
		row.xrReady = true;
	}
	/**
	* The hold row of one (data plane, org, app): `held`, `released`, or `null` when no import ever
	* staged the app there.
	*/
	importHold(dpId, orgId, appSlug) {
		return this.#holds.get(holdKey(dpId, orgId, appSlug)) ?? null;
	}
	/** The reconciler deleted the XR of every `deleting` row: each moves to `deleted`. */
	completeDeletes() {
		for (const row of this.#rows) if (row.state === "deleting") row.state = "deleted";
	}
	/** Sets or clears `BLOB_RELOCATION_MOVER_MEMBER`. Only a later `stage` reads it. */
	setMoverConfigured(configured) {
		this.#moverConfigured = configured;
	}
	/**
	* Every binding row of the data plane, sorted by org, app, logical and generation. A data plane
	* the Fake does not know answers the empty list. Each row is a copy.
	*/
	bindings(dpId) {
		return this.#rows.filter((row) => row.dpId === dpId).sort(compareRows).map((row) => ({
			orgId: row.orgId,
			appSlug: row.appSlug,
			logical: row.logical,
			durability: row.durability,
			generation: row.generation,
			physicalName: row.physicalName,
			location: row.location,
			state: row.state,
			frozen: row.frozen,
			freezeApplied: row.freezeApplied,
			xrReady: row.xrReady,
			served: row.served
		}));
	}
	async stage(dpId, relocationId, body) {
		const params = RelocationParamsSchema.parse({
			dpId,
			relocationId
		});
		const request = BlobRelocationStageBodySchema.parse(body);
		const key = `${params.dpId}/${params.relocationId}`;
		const known = this.#relocations.get(key);
		if (known !== void 0) {
			if (known.phase === "aborted") throw new BlobRelocationRefusal("relocation_aborted");
			if (known.kind !== "blob" || known.orgId !== request.orgId || known.appSlug !== request.appSlug || known.targetLocation !== request.targetLocation) throw new BlobRelocationRefusal("relocation_conflict");
			return answerOf(known);
		}
		if (!this.#moverConfigured) throw new BlobRelocationRefusal("relocation_mover_unconfigured");
		const provider = this.#providers.get(params.dpId);
		if (provider !== void 0 && provider !== "gcp") throw new BlobRelocationRefusal("relocation_provider_unsupported");
		const appRows = this.#rows.filter((row) => row.dpId === params.dpId && row.orgId === request.orgId && row.appSlug === request.appSlug);
		const sources = appRows.filter((row) => row.served && isLiveSource(row));
		if (sources.length === 0) throw new BlobRelocationRefusal("relocation_nothing_to_move");
		const moving = sources.filter((row) => row.location !== request.targetLocation);
		if (moving.length === 0) throw new BlobRelocationRefusal("relocation_same_location");
		if (appRows.some((row) => row.state === "staging")) throw new BlobRelocationRefusal("relocation_conflict");
		if (this.#openImportOf(params.dpId, request.orgId, request.appSlug)) throw new BlobRelocationRefusal("relocation_conflict");
		const pairs = moving.map((source) => ({
			source,
			target: this.#addRow({
				dpId: source.dpId,
				orgId: source.orgId,
				appSlug: source.appSlug,
				logical: source.logical,
				durability: source.durability,
				location: request.targetLocation,
				generation: this.#nextGeneration(this.#identityRows(source.dpId, source.orgId, source.appSlug, source.logical)),
				state: "staging",
				served: true
			})
		}));
		const relocation = {
			kind: "blob",
			dpId: params.dpId,
			orgId: request.orgId,
			appSlug: request.appSlug,
			targetLocation: request.targetLocation,
			phase: "staged",
			pairs
		};
		this.#relocations.set(key, relocation);
		return answerOf(relocation);
	}
	async read(dpId, relocationId) {
		return answerOf(this.#find(dpId, relocationId));
	}
	async freeze(dpId, relocationId) {
		const relocation = this.#find(dpId, relocationId);
		if (relocation.phase === "aborted") throw new BlobRelocationRefusal("relocation_aborted");
		if (relocation.phase === "staged") {
			for (const { source } of relocation.pairs) source.frozen = true;
			relocation.phase = "frozen";
		}
		return answerOf(relocation);
	}
	async switch(dpId, relocationId) {
		const relocation = this.#find(dpId, relocationId);
		if (relocation.phase === "aborted") throw new BlobRelocationRefusal("relocation_aborted");
		if (relocation.phase === "staged") throw new BlobRelocationRefusal("relocation_not_frozen");
		if (relocation.phase === "frozen") {
			if (relocation.pairs.some(({ source }) => source.durability === "durable" && isLiveSource(source) && !readsFrozen(source))) throw new BlobRelocationRefusal("relocation_not_frozen");
			if (relocation.pairs.some(({ target }) => !target.xrReady)) throw new BlobRelocationRefusal("relocation_targets_not_ready");
			for (const { source, target } of relocation.pairs) {
				if (isLiveSource(source)) source.state = "holding";
				target.state = "ready";
			}
			relocation.phase = "switched";
		}
		return answerOf(relocation);
	}
	async releaseSource(dpId, relocationId) {
		const relocation = this.#find(dpId, relocationId);
		if (relocation.phase === "aborted") throw new BlobRelocationRefusal("relocation_aborted");
		if (relocation.phase === "staged" || relocation.phase === "frozen") throw new BlobRelocationRefusal("relocation_not_switched");
		if (relocation.phase === "switched") {
			for (const { source } of relocation.pairs) if (source.state === "holding") {
				source.state = "deleting";
				source.xrReady = false;
			}
			relocation.phase = "released";
		}
		return answerOf(relocation);
	}
	async abort(dpId, relocationId) {
		const params = RelocationParamsSchema.parse({
			dpId,
			relocationId
		});
		const key = `${params.dpId}/${params.relocationId}`;
		if (!this.#relocations.has(key)) this.#tombstone(params.dpId, key);
		const relocation = this.#find(params.dpId, params.relocationId);
		if (relocation.phase === "switched" || relocation.phase === "released") throw new BlobRelocationRefusal("relocation_already_switched");
		if (relocation.phase !== "aborted") {
			for (const { source, target } of relocation.pairs) {
				source.frozen = false;
				source.freezeApplied = false;
				target.state = "deleting";
				target.xrReady = false;
			}
			relocation.phase = "aborted";
		}
		return answerOf(relocation);
	}
	async stageImport(dpId, relocationId, body) {
		const params = RelocationParamsSchema.parse({
			dpId,
			relocationId
		});
		const request = BlobLegacyImportStageBodySchema.parse(body);
		const known = this.#relocations.get(`${params.dpId}/${params.relocationId}`);
		if (known !== void 0) {
			if (known.phase === "aborted") throw new BlobRelocationRefusal("relocation_aborted");
			if (known.kind !== "legacy_import" || known.orgId !== request.orgId || known.appSlug !== request.appSlug) throw new BlobRelocationRefusal("relocation_conflict");
			return this.#importAnswerOf(known);
		}
		if (!this.#moverConfigured) throw new BlobRelocationRefusal("relocation_mover_unconfigured");
		const provider = this.#providers.get(params.dpId);
		if (provider !== void 0 && provider !== "gcp") throw new BlobRelocationRefusal("relocation_provider_unsupported");
		if (!this.#sources.has(params.dpId)) throw new BlobRelocationRefusal("relocation_nothing_to_move");
		if (this.#unknownApps.has(request.appSlug) || this.#unknownOrgs.has(request.orgId)) throw new BlobRelocationRefusal("relocation_nothing_to_move");
		const hold = this.importHold(params.dpId, request.orgId, request.appSlug);
		const appRows = this.#rows.filter((row) => row.dpId === params.dpId && row.orgId === request.orgId && row.appSlug === request.appSlug);
		if (hold === "released" || hold === null && appRows.some((row) => row.served && row.state === "ready" && row.logical !== BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME)) throw new BlobRelocationRefusal("relocation_import_bound");
		if (appRows.some((row) => row.state === "staging") || this.#openImportOf(params.dpId, request.orgId, request.appSlug)) throw new BlobRelocationRefusal("relocation_conflict");
		this.#holds.set(holdKey(params.dpId, request.orgId, request.appSlug), "held");
		const relocation = {
			kind: "legacy_import",
			dpId: params.dpId,
			orgId: request.orgId,
			appSlug: request.appSlug,
			targetLocation: null,
			phase: "staged",
			pairs: []
		};
		this.#relocations.set(`${params.dpId}/${params.relocationId}`, relocation);
		return this.#importAnswerOf(relocation);
	}
	async readImport(dpId, relocationId) {
		return this.#importAnswerOf(this.#findImport(dpId, relocationId));
	}
	async switchImport(dpId, relocationId) {
		const relocation = this.#findImport(dpId, relocationId);
		if (relocation.phase === "aborted") throw new BlobRelocationRefusal("relocation_aborted");
		if (relocation.phase === "staged") {
			const targets = this.#importTargetsOf(relocation);
			const unallocated = this.#unallocatedOf(relocation, targets);
			if (targets.length === 0 || unallocated.length > 0 || targets.some((target) => target.state !== "ready")) throw new BlobRelocationRefusal("relocation_targets_not_ready");
			const key = holdKey(relocation.dpId, relocation.orgId, relocation.appSlug);
			if (this.#holds.has(key)) this.#holds.set(key, "released");
			relocation.phase = "switched";
		}
		return this.#importAnswerOf(relocation);
	}
	async releaseImport(dpId, relocationId) {
		const relocation = this.#findImport(dpId, relocationId);
		if (relocation.phase === "aborted") throw new BlobRelocationRefusal("relocation_aborted");
		if (relocation.phase === "staged") throw new BlobRelocationRefusal("relocation_not_switched");
		relocation.phase = "released";
		return this.#importAnswerOf(relocation);
	}
	async abortImport(dpId, relocationId) {
		const params = RelocationParamsSchema.parse({
			dpId,
			relocationId
		});
		const key = `${params.dpId}/${params.relocationId}`;
		if (!this.#relocations.has(key)) this.#tombstone(params.dpId, key);
		const relocation = this.#findImport(params.dpId, params.relocationId);
		if (relocation.phase === "switched" || relocation.phase === "released") throw new BlobRelocationRefusal("relocation_already_switched");
		relocation.phase = "aborted";
		return this.#importAnswerOf(relocation);
	}
	/**
	* The `blob` relocation of the two ids, after a parse of both; else `relocation_not_found`. An
	* abort tombstone and a legacy import also answer `relocation_not_found`.
	*/
	#find(dpId, relocationId) {
		const params = RelocationParamsSchema.parse({
			dpId,
			relocationId
		});
		const relocation = this.#relocations.get(`${params.dpId}/${params.relocationId}`);
		if (relocation?.kind !== "blob") throw new BlobRelocationRefusal("relocation_not_found");
		return relocation;
	}
	/**
	* The legacy import of the two ids, after a parse of both; else `relocation_not_found`. An abort
	* tombstone and a `blob` relocation also answer `relocation_not_found`.
	*/
	#findImport(dpId, relocationId) {
		const params = RelocationParamsSchema.parse({
			dpId,
			relocationId
		});
		const relocation = this.#relocations.get(`${params.dpId}/${params.relocationId}`);
		if (relocation?.kind !== "legacy_import") throw new BlobRelocationRefusal("relocation_not_found");
		return relocation;
	}
	/** Writes the abort tombstone of one id (R-083 item 4): a later `stage` of either kind answers `relocation_aborted`. */
	#tombstone(dpId, key) {
		this.#relocations.set(key, {
			kind: null,
			dpId,
			orgId: null,
			appSlug: null,
			targetLocation: null,
			phase: "aborted",
			pairs: []
		});
	}
	/** True when an open legacy import holds the (data plane, org, app). */
	#openImportOf(dpId, orgId, appSlug) {
		return [...this.#relocations.values()].some((relocation) => isOpenImport(relocation) && relocation.dpId === dpId && relocation.orgId === orgId && relocation.appSlug === appSlug);
	}
	/**
	* The answer of one legacy import: the shared bucket of its data plane, the hold row, the served
	* `durable` rows of its org and app in `pending` or `ready` in ascending logical order, and the
	* declared `durable` names with no such row (R-111 item 2), parsed with
	* {@link BlobLegacyImportAnswerSchema}.
	*/
	#importAnswerOf(relocation) {
		const hold = this.importHold(relocation.dpId, relocation.orgId, relocation.appSlug);
		if (hold === null) throw new BlobRelocationRefusal("relocation_not_found");
		const targets = this.#importTargetsOf(relocation);
		return BlobLegacyImportAnswerSchema.parse({
			source: this.#sources.get(relocation.dpId),
			hold,
			targets,
			unallocated: this.#unallocatedOf(relocation, targets)
		});
	}
	/**
	* The `unallocated` names of one legacy import (R-111 item 2): each `durable` bucket that
	* {@link declareBucket} or a served {@link seedBucket} declared for its data plane and app, except
	* `claimcheck`, that is not the `logical` of one of `targets`, in ascending order.
	*/
	#unallocatedOf(relocation, targets) {
		const allocated = new Set(targets.map((target) => target.logical));
		return [...this.#declared.get(`${relocation.dpId}/${relocation.appSlug}`) ?? /* @__PURE__ */ new Map()].filter(([logical, durability]) => durability === "durable" && logical !== BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME && !allocated.has(logical)).map(([logical]) => logical).sort();
	}
	/**
	* The targets of one legacy import: the served `durable` rows of its org and app in `pending` or
	* `ready`, except `claimcheck`, in ascending logical order.
	*/
	#importTargetsOf(relocation) {
		const orgId = relocation.orgId;
		const appSlug = relocation.appSlug;
		return this.#rows.filter((row) => row.dpId === relocation.dpId && row.orgId === orgId && row.appSlug === appSlug && row.served && row.durability === "durable" && row.logical !== BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME && isLiveSource(row)).sort(compareRows).map((row) => ({
			logical: row.logical,
			physicalName: row.physicalName,
			location: row.location,
			generation: row.generation,
			state: row.state
		}));
	}
	/** Records the served bucket `logical` of (`dpId`, `appSlug`) with `durability`. */
	#declare(dpId, appSlug, logical, durability) {
		const key = `${dpId}/${appSlug}`;
		const declared = this.#declared.get(key) ?? /* @__PURE__ */ new Map();
		declared.set(logical, durability);
		this.#declared.set(key, declared);
	}
	/** The `blob` relocation of the two ids for a hook; `RangeError` for an unknown id, an abort tombstone or an import. */
	#held(dpId, relocationId) {
		const relocation = this.#relocations.get(`${dpId}/${relocationId}`);
		if (relocation?.kind !== "blob") throw new RangeError(`no relocation ${relocationId} on ${dpId}`);
		return relocation;
	}
	/** Every row of one identity, whatever its state. */
	#identityRows(dpId, orgId, appSlug, logical) {
		return this.#rows.filter((row) => row.dpId === dpId && row.orgId === orgId && row.appSlug === appSlug && row.logical === logical);
	}
	/** `1 + max(generation)` over `rows`, or 1 for no row. */
	#nextGeneration(rows) {
		return rows.reduce((max, row) => Math.max(max, row.generation), 0) + 1;
	}
	/** Adds one row with its physical name from `physicalBucketName()`. */
	#addRow(fields) {
		const physicalName = physicalBucketName({
			dpId: fields.dpId,
			orgId: fields.orgId,
			appSlug: fields.appSlug,
			logical: fields.logical,
			generation: fields.generation
		});
		const row = {
			...fields,
			physicalName,
			frozen: false,
			freezeApplied: false,
			xrReady: false
		};
		this.#rows.push(row);
		return row;
	}
};
/**
* The freeze read-back of one source row (R-082 item 1): the row holds the flag, the reconciler
* applied it, and the row is in a state whose XR serves reads.
*/
function readsFrozen(row) {
	return row.frozen && row.freezeApplied && RELOCATION_FREEZABLE_SOURCE_STATES.includes(row.state);
}
/** The answer of one relocation: its pairs in ascending logical order, parsed with the answer schema. */
function answerOf(relocation) {
	const buckets = [...relocation.pairs].sort((a, b) => a.source.logical < b.source.logical ? -1 : 1).map(({ source, target }) => ({
		logical: source.logical,
		durability: source.durability,
		source: {
			physicalName: source.physicalName,
			location: source.location,
			generation: source.generation,
			state: source.state,
			frozen: readsFrozen(source)
		},
		target: {
			physicalName: target.physicalName,
			location: target.location,
			generation: target.generation,
			state: target.state,
			ready: target.xrReady && (target.state === "staging" || target.state === "ready")
		}
	}));
	return BlobRelocationAnswerSchema.parse({ buckets });
}
//#endregion
export { BLOB_LEGACY_IMPORT_ACTIONS, BLOB_LEGACY_IMPORT_ROUTE, BLOB_RELOCATION_ACTIONS, BLOB_RELOCATION_CLOSED_STATES, BLOB_RELOCATION_ERRORS, BLOB_RELOCATION_ERROR_STATUS, BLOB_RELOCATION_OPEN_STATES, BLOB_RELOCATION_REFUSAL_PATTERN, BLOB_RELOCATION_ROUTE, BLOB_RELOCATION_STATES_CLOSED_KEEP_DAYS, BLOB_RELOCATION_STATE_VALUES, BlobLegacyImportAnswerSchema, BlobLegacyImportStageBodySchema, BlobRelocationActionBodySchema, BlobRelocationAnswerSchema, BlobRelocationRefusal, BlobRelocationStageBodySchema, FakeReleaseEngineRelocationClient, LEGACY_IMPORT_ATTEMPT_ROOT_PATTERN, LEGACY_IMPORT_EXCLUDED_PREFIXES, LEGACY_IMPORT_HOLD_STATES, LEGACY_IMPORT_PREFIXES_MAX, LEGACY_IMPORT_PREFIX_MAX_LENGTH, LEGACY_IMPORT_PREFIX_PATTERN, LEGACY_IMPORT_SKIPPED_STEPS, LEGACY_IMPORT_SOURCE_BUCKET_PATTERN, LEGACY_IMPORT_TARGET_STATES, LegacyImportCreateBodySchema, LegacyImportSourceSchema, LegacyImportTargetSchema, RELOCATIONS_ROUTE, RELOCATION_ABORT_ROUTE, RELOCATION_APPROVE_ROUTE, RELOCATION_FREEZABLE_SOURCE_STATES, RELOCATION_GATES, RELOCATION_HOLD_DAYS, RELOCATION_MOVER_KINDS, RELOCATION_PLAN_LIST_MAX, RELOCATION_PLAN_STATES, RELOCATION_ROUTE, RELOCATION_SOURCE_STATES, RELOCATION_STEPS, RELOCATION_STEP_DETAIL_MAX_LENGTH, RELOCATION_STEP_STATES, RELOCATION_TARGET_STATES, RelocationAbortBodySchema, RelocationApproveBodySchema, RelocationBucketSchema, RelocationBucketSourceSchema, RelocationBucketTargetSchema, RelocationBucketViewSchema, RelocationCreateBodySchema, RelocationDataPlaneParamsSchema, RelocationParamsSchema, RelocationPlanCreateBodySchema, RelocationPlanListViewSchema, RelocationPlanViewSchema, RelocationStepViewSchema, blobLegacyImportPath, blobRelocationPath, blobRelocationRefusalOf, findLegacyImportNameClash, isLegacyImportExcludedKey, isOpenBlobRelocationState, legacyImportPrefixesSchema, legacyImportTargetName };

//# sourceMappingURL=relocation.js.map